/**
 * ============================================================
 * KEJJA OS — src/components/admin/HomeDetailDrawer.tsx
 * ============================================================
 */

import { useEffect } from 'react';
import { X, Home as HomeIcon, Users, Globe, Clock, Loader2 } from 'lucide-react';
import { useAdminHomeDetail } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export default function HomeDetailDrawer({ homeId, onClose }: { homeId: string | null; onClose: () => void }) {
  const { data: h, isLoading } = useAdminHomeDetail(homeId);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!homeId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-slate-950 border-l border-slate-800 z-50 overflow-y-auto text-slate-100 shadow-2xl">

        <div className="sticky top-0 bg-slate-950/90 backdrop-blur border-b border-slate-800 px-5 py-3 flex items-center justify-between z-10">
          <div className="text-sm font-semibold">Home detail</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading || !h ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-5 space-y-5">

            <div>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <HomeIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-bold truncate">{h.name}</h2>
                  {h.motto && <div className="text-xs text-slate-400 italic truncate">"{h.motto}"</div>}
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
              <Field icon={Globe} label="Country">{h.country ?? '—'}</Field>
              {h.region && <Field icon={Globe} label="Region">{h.region}</Field>}
              <Field icon={Globe} label="Currency">{h.currencyCode}</Field>
              <Field icon={Globe} label="Timezone">{h.timezone}</Field>
              <Field icon={Clock} label="Created">{formatDate(h.createdAt)}</Field>
            </div>

            {/* Activity */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Activity</div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Transactions" value={h.activity.financeTransactions} />
                <Metric label="Pantry"        value={h.activity.pantryItems} />
                <Metric label="Routines"      value={h.activity.routinesActive} />
                <Metric label="Events"        value={h.activity.calendarEvents} />
                <Metric label="Communities"   value={h.activity.communitiesJoined} />
                <Metric label="Members"       value={h.members.length} />
              </div>
            </div>

            {/* Members */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Members ({h.members.length})
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
                {h.members.map((m) => (
                  <div key={m.userId} className="p-3 flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0 text-[10px] font-semibold">
                      {m.displayName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {m.displayName}
                        {!m.isActive && <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1 py-0.5 rounded">inactive</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                    </div>
                    <span className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase',
                      m.role === 'owner'  ? 'bg-rose-500/15 text-rose-400' :
                      m.role === 'parent' ? 'bg-sky-500/15 text-sky-400' :
                      'bg-slate-800 text-slate-400',
                    )}>{m.role}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[10px] text-slate-600 text-center pt-2 font-mono">
              {h.id}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Field({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <div className="p-3 flex items-center gap-3">
      <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold w-20">{label}</div>
      <div className="text-xs text-slate-200 flex-1 truncate text-right">{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-md p-2.5">
      <div className="text-lg font-bold tabular-nums">{value.toLocaleString()}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function formatDate(iso: string | Date): string {
  return new Date(iso).toLocaleString('en-KE', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
